import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../firebase/config";
import { useAuth } from "../context/AuthContext";
import Header from "../components/Header";
import BookSearchCarousel from "../components/BookSearchCarousel";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
  addDoc,
  getDocs,
  deleteDoc,
  writeBatch,
  updateDoc,
} from "firebase/firestore";

export default function ChildDetail() {
  const { childId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [child, setChild] = useState(null);
  const [books, setBooks] = useState([]);
  const [showAssign, setShowAssign] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);
  const [totalPages, setTotalPages] = useState("");
  const [targetDate, setTargetDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [assignStatus, setAssignStatus] = useState("active");
  const [editingChildName, setEditingChildName] = useState(false);
  const [newChildName, setNewChildName] = useState("");
  const [childTheme, setChildTheme] = useState("neutral-girl");
  const [childGrade, setChildGrade] = useState("K");
  const [quizSessionSize, setQuizSessionSize] = useState(10);

  useEffect(() => {
    async function fetchChild() {
      const docSnap = await getDoc(doc(db, "users", childId));
      if (docSnap.exists()) {
        const data = docSnap.data();
        setChild({ id: docSnap.id, ...data });
        setChildTheme(data.theme || "neutral-girl");
        setChildGrade(data.grade || "K");
        setQuizSessionSize(data.quizSessionSize || 10);
      }
    }
    fetchChild();
  }, [childId]);

  useEffect(() => {
    const q = query(collection(db, "books"), where("userId", "==", childId));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setBooks(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    });
    return unsubscribe;
  }, [childId]);

  function handleSelect(book) {
    setSelectedBook(book);
    setTotalPages(book.pages || "");
  }

  function handleClear() {
    setSelectedBook(null);
    setTotalPages("");
  }

  async function saveChildSettings() {
    try {
      await updateDoc(doc(db, "users", childId), {
        theme: childTheme,
        grade: childGrade,
        quizSessionSize: quizSessionSize,
      });
      alert("Settings saved!");
    } catch {
      alert("Failed to save settings. Try again.");
    }
  }

  async function assignBook() {
    if (!selectedBook) return alert("Please search for and select a book.");
    if (!totalPages) return alert("Please enter the total number of pages.");
    setLoading(true);
    try {
      await addDoc(collection(db, "books"), {
        userId: childId,
        assignedBy: currentUser.uid,
        title: selectedBook.title,
        author: selectedBook.author,
        coverUrl: selectedBook.coverUrl || null,
        publisher: selectedBook.publisher || "",
        year: selectedBook.year || "",
        totalPages: parseInt(totalPages),
        pagesRead: 0,
        currentPage: 0,
        targetDate: targetDate || null,
        sessions: [],
        createdAt: new Date(),
        status: assignStatus,
      });
      setShowAssign(false);
      setSelectedBook(null);
      setTotalPages("");
      setTargetDate("");
      alert(`${selectedBook.title} has been assigned to ${child?.name}!`);
    } catch {
      alert("Failed to assign book. Try again.");
    }
    setLoading(false);
  }

  function getRecast(book) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const pagesRead = book.pagesRead || 0;
    const remaining = book.totalPages - pagesRead;
    if (remaining <= 0) return { message: "✅ Finished!", color: "#48bb78" };
    if (book.targetDate) {
      const target = new Date(book.targetDate);
      target.setHours(0, 0, 0, 0);
      const daysLeft = Math.ceil((target - today) / (1000 * 60 * 60 * 24));
      if (daysLeft <= 0) return { message: "⚠️ Past due date!", color: "#f56565" };
      const pagesPerDay = Math.ceil(remaining / daysLeft);
      return {
        message: `📖 ${pagesPerDay} pages/day to finish on time`,
        color: pagesPerDay > 30 ? "#ed8936" : "#48bb78"
      };
    }
    if (book.sessions && book.sessions.length > 0) {
      const avgPages = pagesRead / book.sessions.length;
      const daysToFinish = Math.ceil(remaining / avgPages);
      const projectedDate = new Date();
      projectedDate.setDate(projectedDate.getDate() + daysToFinish);
      return {
        message: `📅 On pace to finish by ${projectedDate.toLocaleDateString()}`,
        color: "#4299e1"
      };
    }
    return { message: "📚 No reading sessions yet", color: "#a0aec0" };
  }

  // Counts quizzes on this book waiting for parent review
  function getReviewCount(book) {
    return (book.quizzes || []).filter(
      (q) => q.completedByChild && !q.reviewedByParent && !q.needsRevision
    ).length;
  }

  if (!child) return <div style={styles.loading}>Loading...</div>;

async function deleteChildAccount() {
    if (!window.confirm(`Are you sure you want to delete ${child?.name}'s account? This cannot be undone.`)) return;
    try {
      const booksQuery = query(collection(db, "books"), where("userId", "==", childId));
      const booksSnapshot = await getDocs(booksQuery);
      const batch = writeBatch(db);
      booksSnapshot.docs.forEach((bookDoc) => {
        batch.delete(bookDoc.ref);
      });
      batch.delete(doc(db, "users", childId));
      await batch.commit();
      alert(`${child?.name}'s account has been deleted.`);
      navigate("/parent");
    } catch {
      alert("Failed to delete account. Try again.");
    }
  }

  return (
    <div style={styles.container}>
      <Header />
    <div style={styles.pageHeader}>
        <div>
            <h2 style={styles.childTitle}>{child?.name}'s Books</h2>
            <p style={styles.childSubtitle}>{child?.email}</p>
        </div>
    <button style={styles.assignBtn} onClick={() => setShowAssign(!showAssign)}>
    {showAssign ? "Cancel" : "+ Assign Book"}
    </button>
    </div>
      <div style={styles.content}>
        {/* Assign Book Form */}
        {showAssign && (
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>Assign a Book to {child.name}</h3>
            <div style={styles.statusToggle}>
              <button
                type="button"
                style={assignStatus === "active" ? styles.toggleActive : styles.toggleInactive}
                onClick={() => setAssignStatus("active")}
              >
                📖 Start Reading Now
              </button>
              <button
                type="button"
                style={assignStatus === "queue" ? styles.toggleActive : styles.toggleInactive}
                onClick={() => setAssignStatus("queue")}
              >
                📚 Add to Queue
              </button>
            </div>

            <BookSearchCarousel
              onSelect={handleSelect}
              selectedBook={selectedBook}
              onClear={handleClear}
            />

            <label style={styles.label}>Total Pages</label>
            <input
              style={styles.input}
              type="number"
              placeholder="Number of pages"
              value={totalPages}
              onChange={(e) => setTotalPages(e.target.value)}
            />

            <label style={styles.label}>Target Finish Date (optional)</label>
            <input
              style={styles.input}
              type="date"
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
            />

            <button style={styles.assignBookBtn} onClick={assignBook} disabled={loading}>
              {loading ? "Assigning..." : `Assign to ${child.name}`}
            </button>
          </div>
        )}

        {/* Books List */}
        {books.length === 0 && !showAssign ? (
          <div style={styles.emptyState}>
            <p style={styles.emptyText}>No books assigned yet.</p>
            <p style={styles.emptySubtext}>Tap + Assign Book to get started.</p>
          </div>
        ) : (
          <div style={styles.bookGrid}>
            {books.map((book) => {
              const recast = getRecast(book);
              const reviewCount = getReviewCount(book);
              return (
                <div
                  key={book.id}
                  style={{ ...styles.bookCard, cursor: "pointer" }}
                  onClick={() => navigate(`/parent/child/${childId}/book/${book.id}`)}
                >
                  {reviewCount > 0 && (
                    <span style={styles.reviewBadge}>📬 {reviewCount}</span>
                  )}
                  {book.coverUrl && (
                    <img src={book.coverUrl} alt={book.title} style={styles.cover} />
                  )}
                  <div style={styles.bookInfo}>
                    <h3 style={styles.bookTitle}>{book.title}</h3>
                    <p style={styles.bookAuthor}>{book.author}</p>
                    {book.assignedBy && (
                      <p style={styles.assignedTag}>📋 Assigned by parent</p>
                    )}
                    <div style={styles.progressBar}>
                      <div style={{
                        ...styles.progressFill,
                        width: `${Math.min(((book.pagesRead || 0) / book.totalPages) * 100, 100)}%`
                      }} />
                    </div>
                    <p style={styles.progressText}>
                      {book.pagesRead || 0} of {book.totalPages} pages
                    </p>
                    <div style={{ ...styles.recastBadge, backgroundColor: recast.color }}>
                      <p style={styles.recastMessage}>{recast.message}</p>
                    </div>
                    <p style={styles.tapHint}>Tap to view details →</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

{/* Child Settings Card */}
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>⚙️ {child?.name}'s Settings</h3>

          <label style={styles.label}>Theme</label>
          <div style={styles.themeGrid}>
            {[
              { id: "knight",       label: "⚔️ Knight",    desc: "Sir " + child?.name },
              { id: "princess",     label: "👑 Princess",  desc: "Lady " + child?.name },
              { id: "neutral-boy",  label: "🎒 Scholar (Boy)",  desc: "Mr. Clarke" },
              { id: "neutral-girl", label: "🎒 Scholar (Girl)", desc: "Mrs. Clarke" },
            ].map((t) => (
              <button
                key={t.id}
                style={childTheme === t.id ? styles.themeSelected : styles.themeOption}
                onClick={() => setChildTheme(t.id)}
              >
                <span style={styles.themeLabel}>{t.label}</span>
                <span style={styles.themeDesc}>{t.desc}</span>
              </button>
            ))}
          </div>

          <label style={styles.label}>Grade Level</label>
          <select
            style={styles.input}
            value={childGrade}
            onChange={(e) => setChildGrade(e.target.value)}
          >
            {["K","1st","2nd","3rd","4th","5th","6th","7th","8th","9th","10th","11th","12th"].map((g) => (
              <option key={g} value={g}>{g} Grade</option>
            ))}
          </select>

          <label style={styles.label}>Sight Word Quiz — Words Per Session</label>
          <select
            style={styles.input}
            value={quizSessionSize}
            onChange={(e) => setQuizSessionSize(parseInt(e.target.value))}
          >
            {[5, 10, 15, 20, 25, 30].map((n) => (
              <option key={n} value={n}>{n} words per session</option>
            ))}
          </select>

          <button style={styles.saveSettingsBtn} onClick={saveChildSettings}>
            Save Settings
          </button>
        </div>

      <div style={styles.dangerZone}>
          <h3 style={styles.dangerTitle}>⚠️ Danger Zone</h3>
          <p style={styles.dangerText}>Deleting this account will remove all of {child?.name}'s books and reading history permanently.</p>
          <button style={styles.deleteBtn} onClick={deleteChildAccount}>
            🗑️ Delete {child?.name}'s Account
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: "100vh", backgroundColor: "#f0f4f8" },
  loading: { display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" },
  header: { backgroundColor: "white", padding: "16px 24px", display: "flex", alignItems: "center", gap: 16, boxShadow: "0 2px 4px rgba(0,0,0,0.1)" },
  backBtn: { padding: "8px 16px", backgroundColor: "#e2e8f0", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 14 },
  headerText: { flex: 1 },
  title: { fontSize: 20, color: "#2d3748", margin: 0 },
  subtitle: { fontSize: 13, color: "#718096", margin: 0 },
  assignBtn: { padding: "10px 20px", backgroundColor: "#805ad5", color: "white", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" },
  content: { padding: 24, maxWidth: 700, margin: "0 auto" },
  card: { backgroundColor: "white", borderRadius: 12, padding: 24, marginBottom: 16, boxShadow: "0 2px 4px rgba(0,0,0,0.1)" },
  cardTitle: { fontSize: 18, color: "#2d3748", marginTop: 0, marginBottom: 16 },
  label: { display: "block", fontWeight: "bold", color: "#4a5568", marginBottom: 6, fontSize: 14 },
  input: { width: "100%", padding: 12, borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 16, boxSizing: "border-box", marginBottom: 12 },
  assignBookBtn: { width: "100%", padding: 14, backgroundColor: "#805ad5", color: "white", border: "none", borderRadius: 8, fontSize: 16, cursor: "pointer" },
  emptyState: { textAlign: "center", padding: 60 },
  emptyText: { fontSize: 20, color: "#4a5568" },
  emptySubtext: { fontSize: 14, color: "#a0aec0" },
  bookGrid: { display: "flex", flexDirection: "column", gap: 16 },
  bookCard: { backgroundColor: "white", borderRadius: 12, padding: 20, boxShadow: "0 2px 4px rgba(0,0,0,0.1)", display: "flex", gap: 16, position: "relative" },
  reviewBadge: { position: "absolute", top: 12, right: 12, backgroundColor: "#e53e3e", color: "white", fontSize: 11, fontWeight: "bold", borderRadius: 10, padding: "3px 8px" },
  cover: { width: 80, height: 120, objectFit: "cover", borderRadius: 6 },
  bookInfo: { flex: 1 },
  bookTitle: { fontSize: 18, color: "#2d3748", margin: "0 0 4px 0" },
  bookAuthor: { fontSize: 14, color: "#718096", margin: "0 0 4px 0" },
  assignedTag: { fontSize: 12, color: "#805ad5", margin: "0 0 8px 0" },
  progressBar: { height: 8, backgroundColor: "#e2e8f0", borderRadius: 4, marginBottom: 6 },
  progressFill: { height: "100%", backgroundColor: "#4299e1", borderRadius: 4 },
  progressText: { fontSize: 12, color: "#718096", margin: "0 0 8px 0" },
  recastBadge: { borderRadius: 8, padding: "8px 12px" },
  recastMessage: { color: "white", fontSize: 13, fontWeight: "bold", margin: 0 },
  pageHeader: { backgroundColor: "white", padding: "12px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #e2e8f0", marginBottom: 0 },
  childTitle: { fontSize: 20, color: "#2d3748", margin: "0 0 4px 0" },
  childSubtitle: { fontSize: 13, color: "#718096", margin: 0 },
  statusToggle: { display: "flex", gap: 12, marginBottom: 16 },
  toggleActive: { flex: 1, padding: 12, backgroundColor: "#4299e1", color: "white", border: "none", borderRadius: 8, fontSize: 15, cursor: "pointer", fontWeight: "bold" },
  toggleInactive: { flex: 1, padding: 12, backgroundColor: "#e2e8f0", color: "#4a5568", border: "none", borderRadius: 8, fontSize: 15, cursor: "pointer" },
  dangerZone: { backgroundColor: "white", borderRadius: 12, padding: 24, marginTop: 16, marginBottom: 24, boxShadow: "0 2px 4px rgba(0,0,0,0.1)", borderLeft: "4px solid #f56565" },
  dangerTitle: { fontSize: 16, color: "#c53030", margin: "0 0 8px 0" },
  dangerText: { fontSize: 14, color: "#718096", margin: "0 0 16px 0" },
  deleteBtn: { padding: "12px 24px", backgroundColor: "#f56565", color: "white", border: "none", borderRadius: 8, fontSize: 15, cursor: "pointer", fontWeight: "bold" },
  tapHint: { fontSize: 12, color: "#a0aec0", margin: "8px 0 0 0" },
  themeGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 },
  themeOption: { padding: "10px 12px", backgroundColor: "#f7fafc", border: "2px solid #e2e8f0", borderRadius: 10, cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2 },
  themeSelected: { padding: "10px 12px", backgroundColor: "#ebf8ff", border: "2px solid #4299e1", borderRadius: 10, cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2 },
  themeLabel: { fontSize: 14, fontWeight: "bold", color: "#2d3748" },
  themeDesc: { fontSize: 11, color: "#718096" },
  saveSettingsBtn: { width: "100%", padding: 13, backgroundColor: "#48bb78", color: "white", border: "none", borderRadius: 8, fontSize: 15, cursor: "pointer", fontWeight: "bold" },
};